import type {
  AccessListName,
  AutocompleteOption,
  UserPanelAccessPages,
  UserPanelActionResult,
  UserPanelResponse,
  UserPanelWrite,
} from '@app/application/user-management/types'
import type { JsonFetchOptions } from '@app/utils/types'

import { roleKinds } from '@app/application/user-management/types'
import { SfLog } from '@app/adapters/logger'
import {
  ACCESS_LISTS,
  SEARCH_LIMIT,
  USER_PANEL_REQUEST_KEY,
} from '@app/application/user-management/constants'
import { getSignal } from '@app/adapters/abort'
import SFetch from '@app/utils/sfetch'

// The store turns a rejection into the panel's error state and reads the field
// errors off a 422 body, so every failure has to reach it.
const request = async <R>(
  message: string,
  url: string,
  options?: JsonFetchOptions
): Promise<R | null> => {
  try {
    return await SFetch.json<R>(url, options)
  } catch (error) {
    if (!options?.signal?.aborted) SfLog.error(message, { error })
    throw error
  }
}

export const fetchUser = (id: number): Promise<UserPanelResponse | null> =>
  request(
    'Cannot load the user panel',
    window.Silverfin.Routes.edit_user_path(window.Silverfin.currentFirmId, id, {
      format: 'json',
    }),
    { signal: getSignal(USER_PANEL_REQUEST_KEY) }
  )

export const fetchNewUser = (
  external: boolean
): Promise<UserPanelResponse | null> =>
  request(
    'Cannot load the new user panel',
    window.Silverfin.Routes.new_user_path(window.Silverfin.currentFirmId, {
      format: 'json',
      type: external ? 'external' : 'staff',
    }),
    { signal: getSignal(USER_PANEL_REQUEST_KEY) }
  )

export const createUser = (
  user: UserPanelWrite
): Promise<UserPanelResponse | null> =>
  request(
    'Cannot create the user',
    window.Silverfin.Routes.users_path(window.Silverfin.currentFirmId, {
      format: 'json',
    }),
    { method: 'POST', body: { user } }
  )

export const updateUser = (
  id: number,
  user: UserPanelWrite
): Promise<UserPanelResponse | null> =>
  request(
    'Cannot save the user',
    window.Silverfin.Routes.user_path(window.Silverfin.currentFirmId, id, {
      format: 'json',
    }),
    { method: 'PATCH', body: { user } }
  )

export const toggleActive = (
  id: number,
  isActive: boolean
): Promise<UserPanelActionResult | null> =>
  request(
    'Cannot toggle the user status',
    window.Silverfin.Routes.toggle_active_user_path(
      window.Silverfin.currentFirmId,
      id,
      { format: 'json' }
    ),
    { method: 'POST', body: { isActive } }
  )

export const sendWelcomeMail = (
  id: number
): Promise<UserPanelActionResult | null> =>
  request(
    'Cannot send the welcome email',
    window.Silverfin.Routes.welcome_mail_user_path(
      window.Silverfin.currentFirmId,
      id,
      { format: 'json' }
    ),
    { method: 'POST' }
  )

export const resetPassword = (
  id: number
): Promise<UserPanelActionResult | null> =>
  request(
    'Cannot send the password reset email',
    window.Silverfin.Routes.password_reset_user_path(
      window.Silverfin.currentFirmId,
      id,
      { format: 'json' }
    ),
    { method: 'POST' }
  )

export const resetTwoFactor = (
  id: number
): Promise<UserPanelActionResult | null> =>
  request(
    'Cannot reset two-factor authentication',
    window.Silverfin.Routes.reset_two_factor_authentication_user_path(
      window.Silverfin.currentFirmId,
      id,
      { format: 'json' }
    ),
    { method: 'POST' }
  )

export const resetEmailAddress = (
  id: number
): Promise<UserPanelActionResult | null> =>
  request(
    'Cannot reset the email address',
    window.Silverfin.Routes.reset_email_address_user_path(
      window.Silverfin.currentFirmId,
      id,
      { format: 'json' }
    ),
    { method: 'POST' }
  )

// `CompaniesController#autocomplete` raises on an external role, which is only
// granted from the client file.
export const searchCompanies = (
  userId: number | null,
  q: string,
  role: typeof roleKinds.FIRM | typeof roleKinds.CUSTOMER = roleKinds.FIRM,
  signal?: AbortSignal,
  limit: number = SEARCH_LIMIT
): Promise<AutocompleteOption[] | null> =>
  request(
    'Cannot search client files',
    window.Silverfin.Routes.autocomplete_companies_path({
      firm_id: window.Silverfin.currentFirmId,
      q,
      limit,
      filter: JSON.stringify({ user_id: userId, role }),
    }),
    { signal }
  )

// `GroupsController#autocomplete` leaves out the user's groups when it gets one.
export const searchGroups = (
  userId: number | null,
  q: string,
  signal?: AbortSignal,
  limit: number = SEARCH_LIMIT
): Promise<AutocompleteOption[] | null> =>
  request(
    'Cannot search groups',
    window.Silverfin.Routes.autocomplete_groups_path({
      firm_id: window.Silverfin.currentFirmId,
      q,
      limit,
      ...(userId === null ? {} : { user_id: userId }),
    }),
    { signal }
  )

export const fetchAccessPage = <L extends AccessListName>(
  userId: number,
  list: L,
  q: string,
  page: number,
  signal?: AbortSignal
): Promise<UserPanelAccessPages[L] | null> =>
  request(
    'Cannot load the access list',
    window.Silverfin.Routes.access_user_path(
      window.Silverfin.currentFirmId,
      userId,
      { format: 'json', list: ACCESS_LISTS[list].param, q, page }
    ),
    { signal }
  )
